import React from 'react';
import { Pressable, type StyleProp, type ViewStyle } from 'react-native';
import { useTheme } from '../theme';
import Txt from './Txt';

type BtnVariant = 'default' | 'primary' | 'accent' | 'danger' | 'ghost' | 'outline';
type BtnSize = 'xs' | 'sm' | 'md' | 'lg';

interface ButtonProps {
  variant?: BtnVariant;
  size?: BtnSize;
  block?: boolean;
  /** Icon-only square button. */
  icon?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

const HEIGHT: Record<BtnSize, number> = { xs: 22, sm: 26, md: 32, lg: 38 };
const PADX: Record<BtnSize, number> = { xs: 8, sm: 10, md: 14, lg: 18 };
const FS: Record<BtnSize, 'xs' | 'sm' | 'md' | 'lg'> = { xs: 'xs', sm: 'sm', md: 'md', lg: 'lg' };

/** RN port of theme.css's .t-btn — same heights, variants, and icon/block modifiers. */
export default function Button({
  variant = 'default', size = 'md', block, icon, disabled, onPress, children, style,
}: ButtonProps) {
  const t = useTheme();
  const c = t.colors;

  const bg: Record<BtnVariant, string> = {
    default: c.card, primary: c.ink, accent: c.accent, danger: c.danger,
    ghost: 'transparent', outline: 'transparent',
  };
  const border: Record<BtnVariant, string> = {
    default: c.line, primary: c.ink, accent: c.accent, danger: c.danger,
    ghost: 'transparent', outline: c.line,
  };
  const fg: Record<BtnVariant, string> = {
    default: c.ink, primary: c.paper, accent: '#fff', danger: '#fff',
    ghost: c.ink2, outline: c.ink,
  };

  const h = HEIGHT[size];

  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      style={({ pressed }) => [
        {
          height: h,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          borderRadius: t.radii[2],
          borderWidth: 1,
          borderColor: border[variant],
          backgroundColor: bg[variant],
          paddingHorizontal: icon ? 0 : PADX[size],
          width: icon ? h : undefined,
          alignSelf: block ? 'stretch' : 'flex-start',
          opacity: disabled ? 0.5 : pressed ? 0.85 : 1,
        },
        style,
      ]}
    >
      {typeof children === 'string'
        ? <Txt size={FS[size]} weight="medium" color={fg[variant]}>{children}</Txt>
        : children}
    </Pressable>
  );
}

/** Foreground color a Button uses for a variant — for screens placing an <Icon> inside a button. */
export function buttonFg(variant: BtnVariant, colors: import('../theme').Palette): string {
  const fg: Record<BtnVariant, string> = {
    default: colors.ink, primary: colors.paper, accent: '#fff', danger: '#fff',
    ghost: colors.ink2, outline: colors.ink,
  };
  return fg[variant];
}
